import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './context/AuthContext';

// Pages
import LoginPage from './pages/LoginPage';
import AdminDashboard from './pages/admin/AdminDashboard';
import CompaniesPage from './pages/admin/CompaniesPage';
import CompanyDetailPage from './pages/admin/CompanyDetailPage';
import SensorDetailPage from './pages/admin/SensorDetailPage';
import CompanyDashboard from './pages/company/CompanyDashboard';
import CompanySensorPage from './pages/company/CompanySensorPage';
import ScadaBuilder from './pages/admin/ScadaBuilder';
import CounterDetailPage from './pages/admin/CounterDetailPage';
import CompanyCounterPage from './pages/company/CompanyCounterPage';
import AdminManualDataPage from './pages/admin/AdminManualDataPage';
import CompanyManualDataPage from './pages/company/CompanyManualDataPage';
import AdminStatusSignalsPage from './pages/admin/AdminStatusSignalsPage';
import CompanyStatusControlPage from './pages/company/CompanyStatusControlPage';
import CompanyRemoteAccessPage from './pages/company/CompanyRemoteAccessPage';
import CompanyDataSignalsPage from './pages/company/CompanyDataSignalsPage';
import AdminPlcSettingsPage from './pages/admin/AdminPlcSettingsPage';
import AdminYsaPage from './pages/admin/AdminYsaPage';
import { useLanguage } from './context/LanguageContext';

// Navbar
import Navbar from './components/Navbar';

// Loading
const LoadingPage = () => {
  const { t } = useLanguage();
  return (
    <div className="loading-page">
      <div className="spinner" style={{ width: 48, height: 48 }}></div>
      <p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{t('common.loading')}</p>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, role }) => {
  const { user, loading } = useAuth();
  if (loading) return <LoadingPage />;
  if (!user) return <Navigate to="/login" replace />;
  if (role && user.role !== role) {
    return <Navigate to={user.role === 'admin' ? '/admin' : '/company'} replace />;
  }
  return children;
};

// Layout with Navbar
const WithNavbar = ({ children }) => (
  <div className="app-layout">
    <Navbar />
    <div className="main-content">{children}</div>
  </div>
);

function App() {
  const { user, loading } = useAuth();
  if (loading) return <LoadingPage />;

  return (
    <Router>
      <Routes>
        {/* Public */}
        <Route path="/login" element={
          user ? <Navigate to={user.role === 'admin' ? '/admin' : '/company'} replace /> : <LoginPage />
        } />

        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute role="admin">
            <WithNavbar><AdminDashboard /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/companies" element={
          <ProtectedRoute role="admin">
            <WithNavbar><CompaniesPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/companies/:id" element={
          <ProtectedRoute role="admin">
            <WithNavbar><CompanyDetailPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/scada/:id" element={
          <ProtectedRoute role="admin">
            <WithNavbar><ScadaBuilder /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/sensors/:sensorUUID" element={
          <ProtectedRoute role="admin">
            <WithNavbar><SensorDetailPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/counters/:counterUUID" element={
          <ProtectedRoute role="admin">
            <WithNavbar><CounterDetailPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/companies/:id/manual-data" element={
          <ProtectedRoute role="admin">
            <WithNavbar><AdminManualDataPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/companies/:id/status-signals" element={
          <ProtectedRoute role="admin">
            <WithNavbar><AdminStatusSignalsPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/companies/:id/plc/:plcId" element={
          <ProtectedRoute role="admin">
            <WithNavbar><AdminPlcSettingsPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/admin/companies/:id/ysa" element={
          <ProtectedRoute role="admin">
            <WithNavbar><AdminYsaPage /></WithNavbar>
          </ProtectedRoute>
        } />

        {/* Company Routes */}
        <Route path="/company" element={
          <ProtectedRoute role="company">
            <WithNavbar><CompanyDashboard /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/company/sensors/:sensorUUID" element={
          <ProtectedRoute role="company">
            <WithNavbar><CompanySensorPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/company/counters/:counterUUID" element={
          <ProtectedRoute role="company">
            <WithNavbar><CompanyCounterPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/company/manual-data" element={
          <ProtectedRoute role="company">
            <WithNavbar><CompanyManualDataPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/company/status-control" element={
          <ProtectedRoute role="company">
            <WithNavbar><CompanyStatusControlPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/company/remote-access" element={
          <ProtectedRoute role="company">
            <WithNavbar><CompanyRemoteAccessPage /></WithNavbar>
          </ProtectedRoute>
        } />
        <Route path="/company/data-signals" element={
          <ProtectedRoute role="company">
            <WithNavbar><CompanyDataSignalsPage /></WithNavbar>
          </ProtectedRoute>
        } />

        {/* Default redirect */}
        <Route path="/" element={
          <Navigate to={user ? (user.role === 'admin' ? '/admin' : '/company') : '/login'} replace />
        } />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Router>
  );
}

export default App;
